"use client";

import { CSSProperties, JSX, useMemo } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useTween } from "@/lib/hooks/use-tween";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { BREAKDOWN_MAX_ITEMS, type ModuleBreakdownData } from "@/types/modules";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { chartSlot, formatModuleValue, formatShare } from "./shared/value-format";

function hatchedFill(color: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in oklab, ${color} 32%, transparent)`,
    backgroundImage: `repeating-linear-gradient(135deg, ${color} 0 2px, transparent 2px 5px)`,
  };
}

export default function ModuleBreakdown({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleBreakdownData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const items = useMemo(
    () => (data?.items ?? []).slice(0, BREAKDOWN_MAX_ITEMS),
    [data?.items],
  );
  const format = data?.format ?? "number";
  const label = stripInlineMarkdown(data?.label);
  const footnote = stripInlineMarkdown(data?.footnote);
  const sum = data?.total ?? items.reduce((a, i) => a + i.value, 0);

  const row = useHoverSelect<number>();
  const hasHover = useHoverCapable();
  const active = row.effective;
  const activeItem = active !== null ? items[active] : undefined;

  const shown = useTween(activeItem ? activeItem.value : sum, {
    from: 0,
    duration: activeItem ? 300 : 900,
  });

  return (
    <div
      className={cn("flex w-full flex-col gap-5", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="min-w-0">
        <div className="h-5 truncate text-sm text-[var(--muted)]">
          {activeItem ? stripInlineMarkdown(activeItem.name) : label}
        </div>
        <div className="mt-1.5 flex min-w-0 items-baseline gap-3">
          <span className="truncate text-4xl font-medium tracking-tight tabular-nums text-[var(--fg)]">
            {formatModuleValue(Math.round(shown), { format, locale: intlLocale })}
          </span>
          {activeItem && (
            <span className="shrink-0 text-sm tabular-nums text-[var(--muted)]">
              {t("{share} of total", {
                share: formatShare(activeItem.value / (sum || 1), 0, intlLocale),
              })}
            </span>
          )}
        </div>
      </div>

      <ul className="flex flex-col gap-4 border-t border-dashed border-[var(--border-dashed)] pt-5">
        {items.map((item, i) => {
          const color = chartSlot(i);
          const fraction = Math.min(1, Math.max(0, item.value / (sum || 1)));
          const name = stripInlineMarkdown(item.name);
          const dim = active !== null && active !== i;
          return (
            <li key={item.id ?? i}>
              <button
                type="button"
                aria-pressed={row.selected === i}
                className={cn(
                  "flex w-full flex-col gap-2 text-left transition-opacity duration-200",
                  dim && "opacity-40",
                )}
                onMouseEnter={hasHover ? () => row.setHovered(i) : undefined}
                onMouseLeave={hasHover ? () => row.setHovered(null) : undefined}
                onFocus={hasHover ? () => row.setHovered(i) : undefined}
                onBlur={hasHover ? () => row.setHovered(null) : undefined}
                onClick={(e) =>
                  row.select(i, {
                    kind: "row",
                    id: item.id ?? i,
                    index: i,
                    element: e.currentTarget,
                  })
                }
              >
                <span className="flex w-full items-baseline justify-between gap-4 text-sm">
                  <span
                    className={cn(
                      "min-w-0 truncate",
                      active === i ? "text-[var(--fg)]" : "text-[var(--muted)]",
                    )}
                  >
                    {name}
                  </span>
                  <span className="shrink-0 font-medium tabular-nums text-[var(--fg)]">
                    {formatModuleValue(item.value, { format, locale: intlLocale })}
                  </span>
                </span>
                <span
                  role="progressbar"
                  aria-label={name}
                  aria-valuemin={0}
                  aria-valuemax={sum}
                  aria-valuenow={item.value}
                  className="block h-1.5 w-full overflow-hidden rounded-full bg-[var(--track)]"
                >
                  <span
                    className="block h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{ width: `${fraction * 100}%`, ...hatchedFill(color) }}
                  />
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {footnote && (
        <p className={cn("text-xs leading-relaxed text-[var(--subtle)]", MODULE_LABEL_WRAP.body)}>
          {footnote}
        </p>
      )}
    </div>
  );
}
